import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom/client';

// ============================================================
// Dynamic Injection of Self-Contained Keyframes & Utility Styles
// ============================================================

const STYLES_ID = 'aurora-toast-styles';

const injectStyles = () => {
  if (typeof document === 'undefined' || document.getElementById(STYLES_ID)) return;

  const styleEl = document.createElement('style');
  styleEl.id = STYLES_ID;

  styleEl.textContent = `
    @keyframes toastSlideIn {
      0% {
        opacity: 0;
        transform: translate3d(100px, -12px, 0) scale(0.88) rotateX(8deg);
        filter: blur(10px);
      }
      70% {
        opacity: 1;
        transform: translate3d(-6px, 2px, 0) scale(1.02) rotateX(-2deg);
        filter: blur(0px);
      }
      100% {
        opacity: 1;
        transform: translate3d(0, 0, 0) scale(1) rotateX(0deg);
        filter: blur(0px);
      }
    }

    @keyframes toastSlideOut {
      0% {
        opacity: 1;
        transform: translate3d(0, 0, 0) scale(1);
        max-height: 120px;
        margin-bottom: 0px;
        filter: blur(0px);
      }
      100% {
        opacity: 0;
        transform: translate3d(80px, -24px, 0) scale(0.8) rotateZ(3deg);
        max-height: 0px;
        margin-bottom: -16px;
        padding-top: 0px;
        padding-bottom: 0px;
        filter: blur(8px);
      }
    }

    @keyframes toastProgress {
      0% { stroke-dashoffset: 0; }
      100% { stroke-dashoffset: 100; }
    }

    @keyframes iconPop {
      0% { transform: scale(0.4) rotate(-45deg); opacity: 0; }
      70% { transform: scale(1.25) rotate(8deg); opacity: 1; }
      100% { transform: scale(1) rotate(0deg); opacity: 1; }
    }

    @keyframes ambientGlowPulse {
      0%, 100% { opacity: 0.45; transform: scale(1); }
      50% { opacity: 0.75; transform: scale(1.08); }
    }

    .aurora-toast-enter {
      animation: toastSlideIn 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    .aurora-toast-leave {
      animation: toastSlideOut 0.42s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }

    .aurora-icon-anim {
      animation: iconPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    .aurora-glow {
      animation: ambientGlowPulse 4s ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
      .aurora-toast-enter,
      .aurora-toast-leave,
      .aurora-icon-anim,
      .aurora-glow {
        animation: none !important;
        transition: opacity 0.2s linear !important;
      }
    }
  `;

  document.head.appendChild(styleEl);
};


// ============================================================
// Singleton Listener Architecture with Auto-Mounting
// ============================================================

let containerRoot = null;
let pendingToasts = [];

const toastEvents = {
  listeners: [],

  subscribe(fn) {
    this.listeners.push(fn);

    // Deliver notifications emitted before the container subscribed.
    if (pendingToasts.length > 0) {
      const queuedToasts = [...pendingToasts];
      pendingToasts = [];

      queuedToasts.forEach((toastData) => {
        fn(toastData);
      });
    }

    return () => {
      this.listeners = this.listeners.filter(
        (listener) => listener !== fn
      );
    };
  },

  emit(toastData) {
    injectStyles();

    if (typeof document === 'undefined') return;

    // Auto-mount the toast container if it doesn't exist.
    if (!containerRoot) {
      let targetNode = document.getElementById('aurora-toast-root');

      if (!targetNode) {
        targetNode = document.createElement('div');
        targetNode.id = 'aurora-toast-root';
        document.body.appendChild(targetNode);
      }

      containerRoot = ReactDOM.createRoot(targetNode);
      containerRoot.render(<ToastContainer />);
    }

    // Queue the toast if React has not subscribed yet.
    if (this.listeners.length === 0) {
      pendingToasts.push(toastData);
      return;
    }

    // Deliver immediately. No artificial timeout.
    this.listeners.forEach((fn) => {
      fn(toastData);
    });
  },
};


// ============================================================
// Preserved Imperative API
// ============================================================

export const toast = {
  success: (msg, duration = 3500) =>
    toastEvents.emit({ type: 'success', msg, duration }),

  error: (msg, duration = 4000) =>
    toastEvents.emit({ type: 'error', msg, duration }),

  warning: (msg, duration = 3800) =>
    toastEvents.emit({ type: 'warning', msg, duration }),

  info: (msg, duration = 3500) =>
    toastEvents.emit({ type: 'info', msg, duration }),
};


// ============================================================
// Icons
// ============================================================

const ICONS = {
  success: (
    <svg
      className="w-3.5 h-3.5 sm:w-4 sm:h-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),

  error: (
    <svg
      className="w-3.5 h-3.5 sm:w-4 sm:h-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  ),

  warning: (
    <svg
      className="w-3.5 h-3.5 sm:w-4 sm:h-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),

  info: (
    <svg
      className="w-3.5 h-3.5 sm:w-4 sm:h-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
};


// ============================================================
// Toast Themes
// ============================================================

const THEMES = {
  success: {
    gradientId: 'aurora-grad-success',
    gradStart: '#34d399',
    gradEnd: '#059669',
    iconContainer:
      'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 shadow-[0_0_15px_rgba(52,211,153,0.3)]',
    ambientGlow: 'from-emerald-500/20 via-emerald-600/10 to-transparent',
    accentLine:
      'bg-gradient-to-r from-emerald-400/50 via-emerald-500/20 to-transparent',
  },

  error: {
    gradientId: 'aurora-grad-error',
    gradStart: '#f87171',
    gradEnd: '#dc2626',
    iconContainer:
      'bg-rose-500/15 text-rose-400 border-rose-500/30 shadow-[0_0_15px_rgba(248,113,113,0.3)]',
    ambientGlow: 'from-rose-500/20 via-rose-600/10 to-transparent',
    accentLine:
      'bg-gradient-to-r from-rose-400/50 via-rose-500/20 to-transparent',
  },

  warning: {
    gradientId: 'aurora-grad-warning',
    gradStart: '#fbbf24',
    gradEnd: '#d97706',
    iconContainer:
      'bg-amber-500/15 text-amber-400 border-amber-500/30 shadow-[0_0_15px_rgba(251,191,36,0.3)]',
    ambientGlow: 'from-amber-500/20 via-amber-600/10 to-transparent',
    accentLine:
      'bg-gradient-to-r from-amber-400/50 via-amber-500/20 to-transparent',
  },

  info: {
    gradientId: 'aurora-grad-info',
    gradStart: '#38bdf8',
    gradEnd: '#0284c7',
    iconContainer:
      'bg-sky-500/15 text-sky-400 border-sky-500/30 shadow-[0_0_15px_rgba(56,189,248,0.3)]',
    ambientGlow: 'from-sky-500/20 via-sky-600/10 to-transparent',
    accentLine:
      'bg-gradient-to-r from-sky-400/50 via-sky-500/20 to-transparent',
  },
};


// ============================================================
// Individual Toast
// ============================================================

const ToastItem = ({
  id,
  type,
  msg,
  duration = 3500,
  onClose,
}) => {
  const [isLeaving, setIsLeaving] = useState(false);
  const timerRef = useRef(null);

  const theme = THEMES[type] || THEMES.info;

  const dismiss = useCallback(() => {
    if (isLeaving) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setIsLeaving(true);

    setTimeout(() => {
      onClose(id);
    }, 400);
  }, [isLeaving, id, onClose]);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      dismiss();
    }, duration);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [duration, dismiss]);

  return (
    <div
      className={`group pointer-events-auto relative w-[280px] sm:w-[380px] rounded-xl sm:rounded-2xl p-2.5 sm:p-4 transition-all duration-300 ease-out hover:-translate-y-1 ${
        isLeaving ? 'aurora-toast-leave' : 'aurora-toast-enter'
      }`}
      style={{
        background:
          'linear-gradient(135deg, rgba(15, 23, 42, 0.82) 0%, rgba(30, 41, 59, 0.70) 100%)',
        backdropFilter: 'blur(20px) saturate(190%)',
        WebkitBackdropFilter: 'blur(20px) saturate(190%)',
        boxShadow: `
          0 20px 40px -15px rgba(0, 0, 0, 0.65),
          0 0 0 1px rgba(255, 255, 255, 0.1) inset,
          0 1px 0 0 rgba(255, 255, 255, 0.15) inset
        `,
      }}
    >
      {/* Background Ambient Glow */}
      <div
        className={`aurora-glow absolute -inset-1 -z-10 rounded-xl sm:rounded-2xl bg-gradient-to-r ${theme.ambientGlow} blur-xl pointer-events-none`}
      />

      {/* Top Glass Refraction Accent */}
      <div
        className={`absolute top-0 left-6 right-6 h-[1px] ${theme.accentLine} pointer-events-none`}
      />

      {/* SVG Perimeter Progress Bar */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-10 overflow-visible">
        <defs>
          <linearGradient
            id={theme.gradientId}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor={theme.gradStart} />
            <stop offset="100%" stopColor={theme.gradEnd} />
          </linearGradient>

          <filter
            id={`glow-${id}`}
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feComposite
              in="SourceGraphic"
              in2="blur"
              operator="over"
            />
          </filter>
        </defs>

        <rect
          rx="12"
          ry="12"
          x="1"
          y="1"
          width="calc(100% - 2px)"
          height="calc(100% - 2px)"
          pathLength="100"
          stroke={`url(#${theme.gradientId})`}
          filter={`url(#glow-${id})`}
          className="fill-none stroke-[2px] [stroke-dasharray:100] [stroke-dashoffset:0]"
          style={{
            animation: `toastProgress ${duration}ms linear forwards`,
            animationPlayState: isLeaving ? 'paused' : 'running',
            opacity: isLeaving ? 0.3 : 0.9,
            transition: 'opacity 0.3s ease',
          }}
        />
      </svg>

      {/* Content Layout */}
      <div className="relative z-20 flex items-center sm:items-start gap-2.5 sm:gap-3.5">
        <div
          className={`aurora-icon-anim flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl border shrink-0 ${theme.iconContainer}`}
        >
          {ICONS[type] || ICONS.info}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium leading-snug text-slate-100 tracking-wide break-words select-none">
            {msg}
          </p>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// Toast Container
// ============================================================

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    return toastEvents.subscribe((newToast) => {
      const id = `${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      setToasts((prev) => [
        ...prev,
        { ...newToast, id },
      ]);
    });
  }, []);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[99999] flex flex-col items-end gap-2 sm:gap-3 max-w-[calc(100vw-2rem)] pointer-events-none">
      {toasts.map((t) => (
        <ToastItem
          key={t.id}
          {...t}
          onClose={removeToast}
        />
      ))}
    </div>
  );
}